import { EmployeeSortOptions } from '@dap/common/types';
import { AppBasic, OrgUnitRole, Role, Roles } from '../commonTypes';
import { DataownerBasic } from './dataownerTypes';
import { LocationBasic } from './locationTypes';
import { BrandBasic } from './brandTypes';
import { UserLanguage } from './userTypes';
import { DataownerMinimalSearchDTO, LocationMinimalSearchDTO } from '@dap/generated-types';

export interface EmployeeBase {
  userId: string;
  email: string;
  name: string;
  familyName: string;
  givenName: string;
  phoneNumber: string | null;
  jobTitle: string | null;
  superuser: boolean;
  apps: Array<AppBasic>;
  roles: Array<OrgUnitRole>;
  mgEmployee: boolean;
  status: Array<EmployeeStatus> | string;
}

export interface EmployeeDetail extends EmployeeBase {
  dataowners: Array<DataownerBasic>;
  locations: Array<LocationBasic>;
  brands: Array<BrandBasic>;
  apps: Array<AppBasic>;
  metadata: UserMetadata;
}

export type UserMetadata = {
  verifyEmailSent: string | null;
  superuser: boolean;
  preferredLanguage: UserLanguage;
  createdAt: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  disabledAt: string | null;
};

export interface CreateEmployee {
  email: string;
  familyName?: string;
  givenName?: string;
  jobTitle?: string;
  phoneNumber?: string;
  apps: Array<string>;
  brands: Array<string>;
  dataowners: Array<string>;
  locations: Array<string>;
  roles: Array<Role>;
  emailVerified: boolean;
  verifyEmail: boolean;
  passwordResetEmail?: boolean;
}

export interface PatchEmployeeDto {
  email?: string;
  phoneNumber?: string;
  givenName?: string;
  familyName?: string;
  jobTitle?: string;
  preferredLanguage?: string;
}

export interface EmployeeSearchResult {
  name: string;
  givenName: string;
  familyName: string;
  userId: string;
  email: string;
  status: EmployeeStatus;
  jobTitle: string;
  locationNames: string[];
  phoneNumber?: string;
  mgEmployee?: boolean;
  dataownerNames: string[];
  roles: Array<OrgUnitRole>;
  locations?: Array<LocationMinimalSearchDTO>;
  dataowners?: Array<DataownerMinimalSearchDTO>;
}

export enum EmployeeStatus {
  BLOCKED = 'BLOCKED',
  DISABLED = 'DISABLED',
  TEMP = 'TEMP',
  UNVERIFIED = 'UNVERIFIED',
  ENABLED = 'ENABLED',
  UNKNOWN = 'UNKNOWN',
}

export interface EmployeeSearchDTO {
  appFilter?: string[];
  brandFilter?: string[];
  dataownerFilter?: string[];
  deep?: boolean;
  enabledStatusFilter?: string[];
  locationFilter?: string[];
  locationPropertyFilter?: string[];
  query?: string;
  regionFilter?: string[];
  roleFilter?: string[];
  sort?: EmployeeSortOptions;
}

export interface EmployeeBasicV2 extends EmployeeBase {
  userId: string;
  status: string;
  locationNames: Array<string>;
}

export interface EmployeeOverview {
  activeEmployees: Array<EmployeeBasicV2>;
  inactiveEmployees: Array<EmployeeBasicV2>;
}

export interface AddAppToEmployee {
  userId: string;
  apps: Array<string>;
}

export type RemoveAppFromEmployee = AddAppToEmployee;

export interface AddRolesToEmployee {
  userId: string;
  roles: Array<Role>;
}

export interface UpdateEmployeeRoles {
  userId: string;
  roles: Array<Roles>;
}

export type RemoveRolesFromEmployee = AddRolesToEmployee;

export enum MenuAction {
  add = 'add',
  remove = 'remove',
}

export interface EmployeeActionHandlers<T extends EmployeeBase> {
  onEdit?: (employee: T) => void;
  onResetPassword?: (employee: T) => void;
  onRemove?: (employee: T) => void;
  onViewLog?: (employee: T) => void;
  onViewUserData?: (employee: T) => void;
  onUpdateUserData?: (employee: T) => void;
  onActivate?: (employee: T) => void;
  onDeactivate?: (employee: T) => void;
  onDeleteUser?: (employee: T) => void;
  onRemoveFromBrand?: (employee: T) => void;
  onRemoveFromLocation?: (employee: T) => void;
}
